import React, { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import { AlertTriangle, ArrowLeft, ChevronDown, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  useCreateExternalMetadataConfig,
  useGetMetadataConfigByModel,
  useMigrateLegacyConfig,
  useUpdateExternalMetadataConfig,
} from './admin-external-metadata-api'
import type { AiModel, LegacyExternalMetadataSetting } from './admin-manage-models'
import { ExternalMetadataConfigForm, type ConfigFormValues } from './external-metadata-config-form'

type LegacySettingsViewProps = {
  settings: LegacyExternalMetadataSetting[]
  onMigrate: () => void
  isMigrating: boolean
}

const LegacySettingsView = ({ settings, onMigrate, isMigrating }: LegacySettingsViewProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.external_metadata' })
  return (
    <Box>
      <Alert
        severity="warning"
        icon={<AlertTriangle size={20} />}
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={onMigrate}
            disabled={isMigrating}
            startIcon={isMigrating ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
          >
            {isMigrating ? t('migrating') : t('migrate_button')}
          </Button>
        }
      >
        <Typography variant="body2">{t('legacy_settings_info')}</Typography>
      </Alert>

      {settings.map((setting, index) => (
        <Paper key={setting.name || index} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2">{setting.title}</Typography>
            <Tooltip title={t('legacy_setting_tooltip')}>
              <Chip size="small" label={t('legacy_label')} color="warning" variant="outlined" />
            </Tooltip>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>{t('field_name_label')}:</strong> {setting.name}
          </Typography>

          {setting.placeholder && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>{t('placeholder_label')}:</strong> {setting.placeholder}
            </Typography>
          )}

          {setting.options && setting.options.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>{t('options_label')}:</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {setting.options.map((opt, optIndex) => (
                  <Chip
                    key={optIndex}
                    size="small"
                    label={`${opt.text} (${opt.value})`}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {setting.multiple_options && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>{t('multiple_selections_label')}:</strong> {t('enabled_label')}
            </Typography>
          )}
        </Paper>
      ))}
    </Box>
  )
}

type AdminModelMetadataConfigProps = {
  model: AiModel
  onBack: () => void
}

export const AdminModelMetadataConfig = ({ model, onBack }: AdminModelMetadataConfigProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.external_metadata' })
  const { t: tCommon } = useTranslation('common')
  const [showMigrateDialog, setShowMigrateDialog] = useState(false)
  const unifiedQuery = useGetMetadataConfigByModel(model.id)
  const createConfigMutation = useCreateExternalMetadataConfig()
  const updateConfigMutation = useUpdateExternalMetadataConfig()
  const migrateLegacyMutation = useMigrateLegacyConfig()

  const handleSubmit = async (data: ConfigFormValues) => {
    const settingsPayload = data.settings.map((setting) => {
      if (setting.type === 'options') {
        return {
          type: 'options' as const,
          name: setting.name,
          title: setting.title,
          options: setting.options,
          placeholder: setting.placeholder,
          multiple_options: setting.multiple_options,
        }
      }
      return {
        type: 'text' as const,
        name: setting.name,
        title: setting.title,
        placeholder: setting.placeholder,
        max_length: setting.max_length,
      }
    })

    const existingConfigId = unifiedQuery.data?.config?.id
    if (existingConfigId) {
      await updateConfigMutation.mutateAsync({
        configId: existingConfigId,
        body: {
          name: data.name,
          settings: settingsPayload,
        },
      })
    } else {
      const linkPayload =
        model.modelType === 'profile' ? { profileId: model.id } : { aiModelId: model.id }
      await createConfigMutation.mutateAsync({
        name: data.name,
        settings: settingsPayload,
        ...linkPayload,
      })
    }
    onBack()
  }

  const isLoading =
    unifiedQuery.isLoading ||
    createConfigMutation.isPending ||
    updateConfigMutation.isPending ||
    migrateLegacyMutation.isPending

  const handleMigrate = () => {
    migrateLegacyMutation.mutate(model.id, {
      onSuccess: () => {
        setShowMigrateDialog(false)
      },
    })
  }

  if (unifiedQuery.isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  const legacySettings = unifiedQuery.data?.legacy
  const configData = unifiedQuery.data?.config
  const hasLegacySettings = legacySettings && legacySettings.length > 0

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowLeft size={16} />} onClick={onBack} sx={{ mb: 2 }}>
        {t('back_to_models_button')}
      </Button>

      <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
        {t('create_custom_metadata_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('create_custom_metadata_description')}
      </Typography>

      {hasLegacySettings && (
        <Accordion
          sx={{ mb: 3, boxShadow: 'none', border: 1, borderColor: 'divider' }}
          defaultExpanded={false}
        >
          <AccordionSummary expandIcon={<ChevronDown size={20} />}>
            <Typography variant="subtitle2">{t('legacy_configuration_title')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <LegacySettingsView
              settings={legacySettings}
              onMigrate={() => setShowMigrateDialog(true)}
              isMigrating={migrateLegacyMutation.isPending}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Dialog open={showMigrateDialog} onClose={() => setShowMigrateDialog(false)}>
        <DialogTitle>{t('migrate_legacy_settings_dialog_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('migrate_legacy_settings_dialog_content')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowMigrateDialog(false)}
            disabled={migrateLegacyMutation.isPending}
          >
            {tCommon('actions.cancel')}
          </Button>
          <Button
            onClick={handleMigrate}
            variant="contained"
            disabled={migrateLegacyMutation.isPending}
            startIcon={migrateLegacyMutation.isPending && <CircularProgress size={16} />}
          >
            {migrateLegacyMutation.isPending ? t('migrating') : t('migrate_button')}
          </Button>
        </DialogActions>
      </Dialog>

      <ExternalMetadataConfigForm
        initialData={configData}
        onSubmit={handleSubmit}
        onCancel={onBack}
        isLoading={isLoading}
      />
    </Box>
  )
}
